import { $, _, tinycolor } from "js/vendor";
import { app } from "js/namespaces";
import { PaletteColorType, BackgroundStyleType, TrayType, ForeColorType } from "common/constants";
import { SideBar } from "js/editor/ui";
import "css/controls.scss";
import { EditThemePanel } from "js/editor/editorPanels/editThemePanel";
import { ds } from "../../core/models/dataService";
import { FeatureType } from "../../../common/features";
import { trackActivity } from "../../core/utilities/utilities";
import { ShowDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import { ChooseThemeDialog } from "js/editor/dialogs/chooseThemeDialog";
import Logos from "js/core/models/logos";
import { getLogoPath } from "common/assetUtils";

const ThemePanel = SideBar.extend({
    className: "editor_menu theme_panel",
    type: "ThemePanel",
    lockSlide: true,

    initialize: function() {
    },

    getSlideColorsLabel: function() {
        if (app.currentCanvas.layouter.elements.primary.getCustomSlideColorsLabel) {
            return app.currentCanvas.layouter.elements.primary.getCustomSlideColorsLabel();
        } else {
            return "Slide Color";
        }
    },

    getBackgroundColorsLabel: function() {
        if (app.currentCanvas.layouter.elements.primary.getCustomBackgroundColorsLabel) {
            return app.currentCanvas.layouter.elements.primary.getCustomBackgroundColorsLabel();
        } else {
            return "Background";
        }
    },

    getSlideColors: function() {
        if (app.currentCanvas.layouter.elements.primary.getCustomSlideColors) {
            return app.currentCanvas.layouter.elements.primary.getCustomSlideColors();
        } else {
            let allowColors = false;
            let currentBackgroundStyle = app.currentCanvas.layouter.elements.background.canvasBackgroundStyle;
            if (currentBackgroundStyle.equalsAnyOf(BackgroundStyleType.LIGHT, BackgroundStyleType.DARK, BackgroundStyleType.ACCENT)) {
                allowColors = true;
            } else if (currentBackgroundStyle == BackgroundStyleType.IMAGE) {
                allowColors = app.currentCanvas.layouter.elements.background.backgroundImage?.colorStyle != "color";
            }

            let slideColors = [];
            _.each(app.currentTheme.palette.getSlideColors(), (color, key) => {
                slideColors.push({
                    name: key,
                    color: color,
                    enabled: allowColors
                });
            });
            slideColors.push({
                name: "colorful",
                enabled: allowColors
            });
            slideColors.push({ name: "auto", enabled: true });

            return slideColors;
        }
    },

    getBackgroundColors: function() {
        if (app.currentCanvas.layouter.elements.primary.getCustomBackgroundColors) {
            return app.currentCanvas.layouter.elements.primary.getCustomBackgroundColors();
        } else {
            let backgroundColors = [];
            _.each(app.currentTheme.palette.getBackgroundColors(app.currentCanvas.slideTemplate.allowColorBackgrounds), (color, key) => {
                backgroundColors.push({
                    name: key,
                    color: color,
                    enabled: true
                });
            });
            return backgroundColors;
        }
    },

    createColorGroup: function(colors, label, selectedColor, className, onColorSelected) {
        let $colorGroup = $.div("color_group").addClass(className);
        $colorGroup.append($.div("label", label));

        let $colorList = $colorGroup.addEl($.div("color_list"));
        let $colorfulChit, $gradientContainer, $autoColor;

        let canvasBackground = app.currentCanvas.getBackgroundColor().toRgbString();

        for (let color of colors) {
            switch (color.name) {
                case "colorful":
                    $colorfulChit = $colorList.addEl($.div("colorful_chit color_chit can-disable").attr("data-color-name", "colorful"));

                    $gradientContainer = $colorfulChit.addEl($.div("container"));
                    for (let color of app.currentTheme.palette.getColorfulColors()) {
                        $gradientContainer.append($.div().css("background", app.currentTheme.palette.getColor(color).toRgbString()));
                    }
                    $colorfulChit.toggleClass("disabled", !color.enabled);
                    if (!color.enabled) {
                        $colorfulChit.addEl($.div("disabled-chit"));
                    }
                    break;
                case "auto":
                    $autoColor = $colorList.addEl(
                        $.div("color_chit")
                            .attr("data-color-name", "neutral")
                            .css("color", app.currentTheme.palette.getForeColor("primary", null, app.currentCanvas.getBackgroundColor()))
                    );
                    $autoColor.append($.div("color_label", "AUTO"));
                    $autoColor.toggleClass("disabled", !color.enabled);
                    break;
                default:
                    let $chit = $colorList.addEl($.div("color_chit can-disable")
                        .attr("data-color-name", color.name)
                        .css("color", color.color)
                        .toggleClass("light-color", tinycolor(color).getLuminance() > 0.95)
                        .toggleClass("disabled", !color.enabled)
                        .append($.div("background").css({ background: canvasBackground })));

                    if (!color.enabled) {
                        $chit.append($.div("disabled-chit"));
                    }
            }
        }

        $colorGroup.find("[data-color-name='" + selectedColor + "']").addClass("selected");

        $colorGroup.on("click", ".color_chit, .colorful_chit", event => {
            if (!$(event.currentTarget).closest(".color_list").is(".disabled")) {
                onColorSelected($(event.currentTarget).attr("data-color-name"));
                $colorGroup.find(".selected").removeClass("selected");
                $(event.currentTarget).closest(".color_chit").addClass("selected");
            }
        });

        return $colorGroup;
    },

    getEditThemePanelOptions: function() {
        const options = {
            theme: app.currentTheme,
            onSuccess: theme => {
                app.themeManager.switchTheme(theme);
            },
        };
        const workspaceId = ds.selection.presentation.getWorkspaceId();
        //if the presentation is using a team theme and belongs to an org...
        if (ds.selection.presentation.has("sharedThemeId") &&
            workspaceId !== "personal") {
            const defaultTeam = ds.teams.defaultTeamForOrg(workspaceId);
            const presentationSharedThemeId = ds.selection.presentation.get("sharedThemeId");

            //If the librarian/owner is part of the same org as the team theme, open the team theme editor...
            if (app.user.checkIfUserAndPresentationBelongToSameOrg(workspaceId) &&
                app.user.features.isFeatureEnabled(FeatureType.EDIT_LIBRARY_ITEMS, workspaceId) &&
                defaultTeam.checkIfSharedThemeBelongsToOrg(presentationSharedThemeId)) {
                options.isSharedThemeEditor = true;
                options.onClosed = () => {
                    const props = {
                        workspace_id: workspaceId,
                        theme_id: presentationSharedThemeId
                    };
                    trackActivity("OrgTheme", "Edited", null, null, props);
                };
            }
        }

        return options;
    },

    renderSlideColors: function() {
        this.$slideColors.empty();
        this.$slideColors.append(this.createColorGroup(this.getSlideColors(), this.getSlideColorsLabel(), app.currentCanvas.model.layout.slideColor, "foreground", color => {
            app.currentCanvas.model.layout.slideColor = color;
            app.currentCanvas.getCanvasElement().resetUserColors();
            app.currentCanvas.markStylesAsDirty();
            app.currentCanvas.updateCanvasModel(true, true);
        }));
    },

    render: function() {
        this.$el.empty();

        this.$slideColors = this.$el.addEl($.div());
        this.renderSlideColors();

        let canvasBGColor;
        switch (app.currentCanvas.model.layout.backgroundStyle) {
            case BackgroundStyleType.DARK:
                canvasBGColor = PaletteColorType.BACKGROUND_DARK;
                break;
            case BackgroundStyleType.LIGHT:
                canvasBGColor = PaletteColorType.BACKGROUND_LIGHT;
                break;
            case BackgroundStyleType.COLOR:
            default:
                canvasBGColor = app.currentCanvas.model.layout.backgroundColor;
                break;
        }

        this.$el.append(this.createColorGroup(this.getBackgroundColors(), this.getBackgroundColorsLabel(), canvasBGColor, "background", color => {
            switch (color) {
                case PaletteColorType.BACKGROUND_DARK:
                    if (app.currentCanvas.model.layout.backgroundStyle == BackgroundStyleType.COLOR && app.currentCanvas.model.layout.slideColor == ForeColorType.NEUTRAL) {
                        app.currentCanvas.model.layout.slideColor = PaletteColorType.THEME;
                    }
                    app.currentCanvas.model.layout.backgroundStyle = BackgroundStyleType.DARK;
                    break;
                case PaletteColorType.BACKGROUND_LIGHT:
                    if (app.currentCanvas.model.layout.backgroundStyle == BackgroundStyleType.COLOR && app.currentCanvas.model.layout.slideColor == ForeColorType.NEUTRAL) {
                        app.currentCanvas.model.layout.slideColor = PaletteColorType.THEME;
                    }
                    app.currentCanvas.model.layout.backgroundStyle = BackgroundStyleType.LIGHT;
                    break;
                case PaletteColorType.BACKGROUND_ACCENT:
                    if (app.currentCanvas.model.layout.backgroundStyle == BackgroundStyleType.COLOR && app.currentCanvas.model.layout.slideColor == ForeColorType.NEUTRAL) {
                        app.currentCanvas.model.layout.slideColor = PaletteColorType.THEME;
                    }
                    app.currentCanvas.model.layout.backgroundStyle = BackgroundStyleType.ACCENT;
                    break;
                default:
                    app.currentCanvas.model.layout.backgroundStyle = BackgroundStyleType.COLOR;
                    app.currentCanvas.model.layout.slideColor = ForeColorType.NEUTRAL;
            }
            app.currentCanvas.model.layout.backgroundColor = color;
            app.currentCanvas.markStylesAsDirty();
            app.currentCanvas.updateCanvasModel(true, true);
            this.renderSlideColors();
        }));

        let backgroundImages = app.currentTheme.get("backgroundImages");
        if (backgroundImages && backgroundImages.length) {
            this.$bgImages = this.$el.addEl($.div());
            for (let bgImage of backgroundImages) {
                if (bgImage.asset) {
                    Logos.getSignedUrlAndLoad(getLogoPath(bgImage.asset)).then(url => {
                        let $bgImage = $.div("theme-background-image-thumbnail");
                        $bgImage.append($.img(url));
                        this.$bgImages.append($bgImage);

                        $bgImage.on("click", event => {
                            app.currentCanvas.model.layout.backgroundStyle = BackgroundStyleType.IMAGE;
                            app.currentCanvas.model.layout.backgroundColor = bgImage.id;
                            app.currentCanvas.markStylesAsDirty();
                            app.currentCanvas.updateCanvasModel(true, true);
                            this.renderSlideColors();
                        });
                    });
                }
            }
        }

        if (!app.mainView.editorView.isSlideEditor) {
            this.$el.addEl($.hr());

            let $switchButton = this.$el.addEl($.div("button").addClass("edit-theme"));
            $switchButton.append($.icon("cached"));
            $switchButton.append($.label("Switch Theme..."));
            $switchButton.on("click", this._switchTheme.bind(this));

            let $editButton = this.$el.addEl($.div("button").addClass("edit-theme"));
            this.$editButton = $editButton;

            let icon = "palette";
            if (ds.selection.presentation.has("sharedThemeId")) {
                icon = "link";
                $editButton.addClass("team-theme");
            }
            $editButton.append($.icon(icon));

            $editButton.append($.label("Edit Theme..."));
            $editButton.on("click", () => {
                this.close();

                const editDialog = new EditThemePanel(this.getEditThemePanelOptions());
                editDialog.show();
            });

            this._updateThemeButton();
        }

        return this;
    },

    _switchTheme: function() {
        this.close();

        // check if team themes are required
        let onlyTeamThemes = false;
        const orgId = ds.selection.presentation.get("orgId");
        const isUserInOrg = app.user.checkIfUserAndPresentationBelongToSameOrg(orgId);

        if (orgId) {
            if (isUserInOrg) {
                const defaultTeam = ds.teams.defaultTeamForOrg(orgId);
                if (defaultTeam) {
                    onlyTeamThemes = !app.user.features.isFeatureEnabled(FeatureType.WORKSPACE_CAN_ACCESS_CUSTOM_THEMES, orgId);
                }
            } else {
                // note that we *do not* have access to the foreign team to see if it has `isSharedThemeRequired` set
                ShowMessageDialog({
                    title: "Cannot switch theme",
                    message: "This presentation belongs to another organization. Only members of that organization can switch the theme."
                });
                return;
            }
        }

        ShowDialog(ChooseThemeDialog, {
            title: "Switch Theme",
            hideUserThemes: onlyTeamThemes,
            hideBuiltInThemes: onlyTeamThemes,
            callback: theme => {
                app.themeManager.switchTheme(theme);
                this._updateThemeButton();
                const editDialog = new EditThemePanel(this.getEditThemePanelOptions());
                editDialog.show();
            }
        });
    },

    _updateThemeButton: function() {
        const isTeamTheme = ds.selection.presentation.has("sharedThemeId");

        const orgId = ds.selection.presentation.get("orgId");
        let canEditLibrary = false;
        if (orgId) {
            canEditLibrary = app.user.features.isFeatureEnabled(FeatureType.EDIT_LIBRARY_ITEMS, orgId);
        }

        if (isTeamTheme && !canEditLibrary) {
            this.$editButton.addClass("disabled");
        } else {
            this.$editButton.removeClass("disabled");
        }
    }
});

export { ThemePanel };
